import React, { useEffect, useState } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { drawService } from '../../services/drawService';
import { Draw } from '../../types';
import { Icon } from '../../components/participant/Icon';
import { ParticipantTopBar } from '../../components/participant/ParticipantTopBar';
import { participantColors as c, fadeUpKeyframes } from '../../theme/participantTheme';

const MONTHS = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];

/**
 * Sorteios — visão do participante (role USUARIO_FINAL): próximos sorteios
 * agendados e resultados de sorteios já concluídos. Renderizado por
 * Draws.tsx quando user.role === 'USUARIO_FINAL'.
 */
export const DrawsParticipant: React.FC = () => {
  const navigate = useNavigate();
  const [draws, setDraws] = useState<Draw[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    drawService
      .getAllDraws()
      .then(setDraws)
      .catch(() => setDraws([]))
      .finally(() => setLoading(false));
  }, []);

  const upcoming = draws
    .filter((d) => d.status === 'PENDING')
    .sort((a, b) => new Date(a.drawDate).getTime() - new Date(b.drawDate).getTime());
  // Em andamento e concluídos: participante precisa ver ganhadores aprovados
  // assim que a associação divulgar (não só quando o sorteio fechar).
  const results = draws
    .filter((d) => d.status === 'COMPLETED' || d.status === 'IN_PROGRESS')
    .sort((a, b) => new Date(b.drawDate).getTime() - new Date(a.drawDate).getTime());

  return (
    <Box sx={{ minHeight: '100vh', background: c.gradients.pageBg, fontFamily: c.fontFamily, pb: '48px' }}>
      <style>{fadeUpKeyframes}</style>

      <Box sx={{ maxWidth: 620, mx: 'auto', px: 2, display: 'flex', flexDirection: 'column', gap: '18px' }}>
        <ParticipantTopBar />

        <Box
          sx={{
            background: c.surface,
            borderRadius: `${c.radius.card}px`,
            padding: '20px',
            boxShadow: c.shadows.card,
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
          }}
        >
          <Box
            component="button"
            onClick={() => navigate('/dashboard')}
            title="Voltar"
            sx={{
              width: 52,
              height: 52,
              flexShrink: 0,
              border: `2px solid ${c.border}`,
              borderRadius: `${c.radius.tile}px`,
              background: c.surfaceHover,
              color: c.brandDark,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              '&:hover': { background: '#e7f4e7', borderColor: c.borderHover },
            }}
          >
            <Icon name="arrow_back" size={24} />
          </Box>
          <Box>
            <Box component="div" sx={{ fontSize: 25, fontWeight: 800, color: c.textDarkest }}>
              Sorteios
            </Box>
            <Box component="div" sx={{ fontSize: 15, color: c.textSecondary, mt: '2px' }}>
              Próximos sorteios e resultados
            </Box>
          </Box>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress sx={{ color: c.brandMid }} size={48} />
          </Box>
        ) : (
          <>
            {upcoming.length > 0 && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <Box component="div" sx={{ fontSize: 14, fontWeight: 800, color: c.textTertiary, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  Próximos sorteios
                </Box>
                {upcoming.map((draw, i) => {
                  const date = new Date(draw.drawDate);
                  return (
                    <Box
                      key={draw.id}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '16px',
                        background: c.surfaceDark,
                        color: c.onDarkPrimary,
                        borderRadius: `${c.radius.card}px`,
                        padding: '18px 20px',
                        boxShadow: c.shadows.darkHero,
                        animation: `participantFadeUp ${0.3 + i * 0.05}s ease both`,
                      }}
                    >
                      <Box
                        sx={{
                          width: 64,
                          height: 64,
                          flexShrink: 0,
                          borderRadius: `${c.radius.tile}px`,
                          background: 'rgba(255,255,255,0.12)',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <Box component="span" sx={{ fontSize: 22, fontWeight: 800, lineHeight: 1 }}>
                          {date.getDate()}
                        </Box>
                        <Box component="span" sx={{ fontSize: 12.5, fontWeight: 700, textTransform: 'uppercase', color: c.onDarkAccent }}>
                          {MONTHS[date.getMonth()]}
                        </Box>
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box component="div" sx={{ fontSize: 18, fontWeight: 800 }}>
                          {draw.prize?.name || 'Prêmio'}
                        </Box>
                        <Box component="div" sx={{ fontSize: 14.5, fontWeight: 600, color: c.onDarkMuted, mt: '2px' }}>
                          {draw.campaign?.name}
                        </Box>
                        <Box
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px',
                            background: 'rgba(245,176,27,0.18)',
                            color: c.amber,
                            borderRadius: `${c.radius.pill}px`,
                            padding: '4px 12px',
                            fontSize: 13,
                            fontWeight: 800,
                            mt: '8px',
                          }}
                        >
                          <Icon name="schedule" size={14} />
                          Agendado
                        </Box>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            )}

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <Box component="div" sx={{ fontSize: 14, fontWeight: 800, color: c.textTertiary, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                Resultados
              </Box>
              {results.length === 0 ? (
                <Box sx={{ background: c.surface, borderRadius: `${c.radius.card}px`, padding: '32px 24px', textAlign: 'center', boxShadow: c.shadows.card }}>
                  <Icon name="emoji_events" size={44} color={c.iconMuted} />
                  <Box component="div" sx={{ fontSize: 16, color: c.textTertiary, mt: '10px' }}>
                    Nenhum sorteio realizado ainda
                  </Box>
                </Box>
              ) : (
                results.map((draw, i) => {
                  const inProgress = draw.status === 'IN_PROGRESS';
                  return (
                  <Box
                    key={draw.id}
                    component="button"
                    onClick={() => navigate(`/draws/${draw.id}/winners`)}
                    sx={{
                      textAlign: 'left',
                      cursor: 'pointer',
                      background: c.surface,
                      border: 'none',
                      borderRadius: `${c.radius.card}px`,
                      padding: '18px 20px',
                      boxShadow: '0 3px 16px rgba(20,61,24,0.06)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '14px',
                      animation: `participantFadeUp ${0.3 + i * 0.05}s ease both`,
                    }}
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', flexWrap: 'wrap' }}>
                        <Box component="div" sx={{ fontSize: 18, fontWeight: 800, color: c.textDarkest }}>
                          {draw.prize?.name || 'Prêmio'}
                        </Box>
                        <Box
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px',
                            background: inProgress ? c.amberBg : c.surfaceTint,
                            color: inProgress ? c.amberTextDark : c.successText,
                            borderRadius: `${c.radius.pill}px`,
                            padding: '4px 10px',
                            fontSize: 12.5,
                            fontWeight: 800,
                          }}
                        >
                          <Icon name={inProgress ? 'schedule' : 'check_circle'} size={14} />
                          {inProgress ? 'Em andamento' : 'Realizado'}
                        </Box>
                      </Box>
                      <Box component="div" sx={{ fontSize: 14.5, color: c.textSecondary, mt: '2px' }}>
                        {draw.campaign?.name} — {new Date(draw.drawDate).toLocaleDateString('pt-BR')}
                      </Box>
                      <Box component="div" sx={{ fontSize: 13.5, color: c.brandMid, fontWeight: 700, mt: '6px' }}>
                        Ver ganhadores
                      </Box>
                    </Box>
                    <Icon name="chevron_right" size={24} color={c.brandMid} />
                  </Box>
                  );
                })
              )}
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
};
